/**
 * CopyId — the shared hover-reveal copy-to-clipboard affordance for every
 * identifier in the app (ASIN, UID, SKU, UPC/EAN, FNSKU, order id, shipment
 * id, PO number, campaign/adgroup/keyword/target id, rule/run id, …).
 *
 * Renders a small greyish rounded button with the lucide `Copy` glyph
 * (two overlapping rounded squares) outlined in the ClickPay brand purple
 * (`text-accent`). Hidden until the wrapper is hovered or the button
 * receives keyboard focus; flashes a `Check` glyph for ~1.2s after a
 * successful copy. Zero layout shift (both states occupy the same 18x18 box).
 *
 * Reuses the ops clipboard primitive (`copyText` — secure-context
 * `navigator.clipboard` first, hidden-textarea `execCommand('copy')`
 * fallback for non-secure origins) rather than reimplementing it.
 *
 * File contains ONLY this component (+ its prop type) — react-refresh's
 * fast-refresh boundary rule forbids mixing component and non-component
 * exports in the same file (see `SuggestionCells.tsx:1-5`).
 *
 * Single-component file by design — do not add non-component exports here.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { copyText, normalizeCopyText } from '../ops/lib/clipboard'

export interface CopyIdProps {
  /** The identifier to copy. null/undefined/''/'—'/'-' → no button rendered
   *  (see `normalizeCopyText`). */
  value: string | number | null | undefined
  /** Human kind for a11y + tooltip: "ASIN", "UID", "PO#", "Campaign ID"… */
  label: string
  /** Existing rendered identifier (a `<Link>`, styled span, etc.). When
   *  omitted, CopyId renders the value itself as a small mono span. */
  children?: React.ReactNode
  /** Glyph px. 12 default (table cells); 14 for page headers/drawer titles. */
  size?: 12 | 14
  /** Extra classes on the wrapper span. */
  className?: string
}

/** Hover+focus-reveal copy button. Wraps `children` (or self-renders `value`
 *  as mono text when no children are given) alongside a small button that
 *  copies `value` to the clipboard on click. */
export function CopyId({ value, label, children, size = 12, className }: CopyIdProps) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear the pending "reset to idle" timer on unmount so we never setState
  // on an unmounted cell — table rows recycle constantly (row-windowing /
  // filtering / sorting), exactly the hazard AsinCopyCell already guards.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const copyValue = normalizeCopyText(value)
  if (copyValue == null) return <>{children ?? <span className="text-ink-faint">—</span>}</>

  const onCopy = async (e: React.MouseEvent) => {
    // Both are load-bearing: identifiers live inside clickable/expandable
    // rows (onRowClick / expandOnRowClick in both EntityTables) and are
    // frequently themselves a <Link> — preventDefault stops the anchor
    // navigation, stopPropagation stops the row handler.
    e.preventDefault()
    e.stopPropagation()
    const ok = await copyText(copyValue)
    if (!ok) return
    setCopied(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), 1200)
  }

  return (
    <span className={`group/copyid inline-flex items-center gap-1 min-w-0 ${className ?? ''}`}>
      {children ?? <span className="font-mono text-xs truncate">{copyValue}</span>}
      <button
        type="button"
        aria-label={`Copy ${label} ${copyValue}`}
        title={copied ? 'Copied' : `Copy ${label}`}
        onClick={onCopy}
        className={
          'shrink-0 inline-flex items-center justify-center h-[18px] w-[18px] rounded ' +
          'border border-edge bg-surface-3 hover:bg-accent-soft hover:border-accent-edge ' +
          'opacity-0 group-hover/copyid:opacity-100 focus-visible:opacity-100 ' +
          'transition-opacity ' + (copied ? 'opacity-100' : '')
        }
      >
        {copied
          ? <Check size={size} strokeWidth={2.5} aria-hidden className="text-success" />
          : <Copy size={size} strokeWidth={2} aria-hidden className="text-accent" />}
      </button>
    </span>
  )
}
